import React from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { ArrowRight, Crown, Briefcase, House, TreeStructure } from "@phosphor-icons/react";

const LEON_CONCIERGE =
  "/photos/iul/iul-5.png";

const FITS = [
  {
    icon: Briefcase,
    title: "The High-Earning W-2 Professional",
    body: "Maxed 401(k), backdoor Roth, golden handcuffs. The Concierge tier audits what's left — and which legal structures move the next dollar into a tax-free bucket.",
  },
  {
    icon: House,
    title: "The Business Owner",
    body: "Great cash flow. No corporate pension. Net worth tied to the business. We design a personal balance sheet that exists independent of the company.",
  },
  {
    icon: TreeStructure,
    title: "The Family Stewarding Generations",
    body: "$1M+ in investable assets, kids in or near adulthood, real estate, possibly inheritance on the horizon. We engineer the tax-efficient transfer — quietly.",
  },
  {
    icon: Crown,
    title: "The Pre-Retiree (5-10 Years Out)",
    body: "Last chapter of accumulation. Sequence-of-returns risk is real. We build a contractual income floor that lets the rest of your portfolio grow without panic.",
  },
];

export default function Concierge() {
  return (
    <div data-testid="concierge-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />

      <section className="relative pt-36 pb-24 lg:pt-44 lg:pb-32 overflow-hidden border-b border-exodus-border">
        <div className="absolute inset-0 bg-gradient-to-br from-exodus-bg via-exodus-surface to-exodus-bg opacity-90" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-8">
            <p className="eyebrow rise"><span className="gold-divider" />The Concierge Tier</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] rise rise-1">
              When the conversation needs to be <span className="gold-text italic">private</span>.
            </h1>
            <p className="text-lg lg:text-xl text-exodus-muted max-w-xl leading-relaxed rise rise-2">
              For high earners, business owners, and families stewarding generations of wealth — the Concierge tier is a quieter, longer-form engagement. Fewer clients. Deeper work. No drip campaigns.
            </p>
            <div className="flex flex-wrap items-center gap-4 rise rise-3">
              <Link to="/reserve?tier=concierge" data-testid="concierge-cta-primary" className="btn-gold">
                Request a Concierge Audit <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/learn" data-testid="concierge-cta-secondary" className="btn-ghost">
                Start in the Library
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 rise rise-4">
            <div className="card-surface p-2">
              <img src={LEON_CONCIERGE} alt="EXODUS Concierge — private strategy" className="w-full block" />
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <p className="eyebrow"><span className="gold-divider" />Who this is for</p>
        <h2 className="font-display text-4xl lg:text-5xl mt-4 max-w-3xl leading-tight">
          Four profiles where private strategy outperforms public planning.
        </h2>
        <div className="grid md:grid-cols-2 gap-px bg-exodus-border mt-16">
          {FITS.map((f, i) => (
            <div key={i} data-testid={`fit-${i}`} className="bg-exodus-bg p-10 lg:p-12 hover:bg-exodus-surface transition-colors">
              <f.icon size={36} weight="thin" className="text-exodus-gold mb-8" />
              <h3 className="font-display text-3xl leading-tight">{f.title}</h3>
              <p className="text-exodus-muted leading-relaxed mt-5">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="bg-exodus-surface border-y border-exodus-border py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <p className="eyebrow"><span className="gold-divider" />The Engagement</p>
          <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
            What a Concierge engagement actually looks like.
          </h2>
          <div className="mt-16 space-y-px bg-exodus-border">
            {[
              { n: "01", t: "Private Audit (90 min)", b: "We map your assets by tax bucket, liability exposure, income guarantees, and legacy plan. You leave with a one-page diagnostic." },
              { n: "02", t: "Blueprint Build (1-2 weeks)", b: "We design a multi-instrument strategy across IUL, annuity, brokerage, and estate structures — modeled against three economic scenarios." },
              { n: "03", t: "Carrier Shop (independent)", b: "We shop 30+ A-rated carriers for the best terms on each instrument. You see every quote." },
              { n: "04", t: "Implementation & Underwriting", b: "Applications, exams, funding. We handle the carrier-side process and keep you posted weekly." },
              { n: "05", t: "Annual Review (lifetime)", b: "Once a year, we sit down, rebalance, and adjust to life changes. The relationship doesn't end at the signature." },
            ].map((s) => (
              <div key={s.n} className="bg-exodus-bg p-8 lg:p-10 grid grid-cols-12 gap-6">
                <p className="col-span-2 font-mono text-sm gold-text">{s.n} / 05</p>
                <div className="col-span-10">
                  <h3 className="font-display text-2xl mb-3">{s.t}</h3>
                  <p className="text-exodus-muted leading-relaxed">{s.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="max-w-4xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center">
        <p className="eyebrow"><span className="gold-divider" />Quiet Strategy</p>
        <h2 className="font-display text-5xl lg:text-6xl mt-4 leading-[0.95]">
          The work is <span className="gold-text italic">slow</span>.<br />The results compound.
        </h2>
        <p className="text-exodus-muted mt-8 max-w-xl mx-auto leading-relaxed">
          Concierge engagements begin with a $100 refundable deposit. The first conversation determines fit — for both sides.
        </p>
        <Link to="/reserve?tier=concierge" className="btn-gold mt-10" data-testid="concierge-final-cta">
          Reserve the Concierge Audit <ArrowRight size={16} weight="bold" />
        </Link>
      </section>

      <Footer />
    </div>
  );
}
